import { useModal } from '@ebay/nice-modal-react'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ToastMessage } from '../components/common'
import type { ThirdPartyPlatforms } from '../types/common'
import type Resources from '../types/resources'

interface AppleSuccessEvent {
  detail: { authorization: { code: string; id_token: string } }
}

interface AppleErrorEvent {
  detail: { error: string }
}

// The SDK reports machine codes, which used to be the user-facing copy. Each
// known code maps to its own message in the login namespace: the first two are
// the user backing out, the second two are configuration faults they cannot act
// on, so those point at support or another method instead of inviting a retry
// that will keep failing. Anything unmapped falls back to apple-signin-failed —
// Apple can add codes, and an unrecognised one must not surface raw.
// Typed against the login namespace rather than `string`, so a typo or a key
// that never made it into the locale files is a compile error here instead of a
// raw identifier in the toast.
const APPLE_ERROR_KEYS: Record<string, keyof Resources['login']> = {
  popup_closed_by_user: 'apple-popup-closed',
  user_cancelled_authorize: 'apple-cancelled',
  invalid_client: 'apple-invalid-client',
  invalid_request: 'apple-invalid-request',
}

// Wires the Apple JS SDK (web browser OAuth): init + success/failure listeners. On success it
// hands the RAW id_token JWS to onToken — the backend's native_login.php verifies the signature
// against Apple's JWKS and rejects anything else (decoded claims are forgeable, so the old
// callback2.php-style "post the parsed payload" protocol is gone).
const useAppleLogin = (
  setThirdPartyLoading: Dispatch<SetStateAction<ThirdPartyPlatforms | null>>,
  onToken: (idToken: string) => void,
) => {
  const toastMessage = useModal(ToastMessage)
  const { t } = useTranslation('login')

  useEffect(() => {
    // Read off `window` rather than as a bare `AppleID` identifier: the SDK is
    // loaded `async defer`, so on a cold cache it has not executed by the time
    // this effect runs and the global does not exist at all. `?.` guards an
    // undefined *value*, not an undeclared *binding* — the bare identifier threw
    // ReferenceError out of the effect and took the tree down with it.
    const initSdk = () =>
      window.AppleID?.auth?.init({
        clientId: 'com.fun88.mx.client',
        scope: 'name email',
        redirectURI: window.location.origin,
        state: '',
        nonce: '',
        usePopup: true,
      })

    // signIn() needs an initialised SDK, so a script that lands after mount has
    // to be initialised when it arrives or the button opens nothing. The
    // listener goes on before the first attempt, so a script finishing in
    // between is still covered (a second init is idempotent).
    const sdkScript = document.querySelector<HTMLScriptElement>(
      'script[src*="appleid.auth.js"]',
    )
    sdkScript?.addEventListener('load', initSdk)
    initSdk()

    const onSuccess = (event: Event) => {
      const { authorization } = (event as unknown as AppleSuccessEvent).detail
      onToken(authorization.id_token)
    }

    const onError = (event: Event) => {
      // The code stays in the console — it is the only diagnostic Apple gives,
      // and it is no longer anywhere in the UI for support to read back.
      const { error } = (event as unknown as AppleErrorEvent).detail
      console.error('Apple sign-in failed:', error)
      toastMessage.show({
        text: t(APPLE_ERROR_KEYS[error] ?? 'apple-signin-failed'),
        isValid: false,
      })
      setThirdPartyLoading(null)
    }

    // Listen for authorization success.
    document.addEventListener('AppleIDSignInOnSuccess', onSuccess)

    // Listen for authorization failures.
    document.addEventListener('AppleIDSignInOnFailure', onError)

    return () => {
      sdkScript?.removeEventListener('load', initSdk)
      document.removeEventListener('AppleIDSignInOnSuccess', onSuccess)
      document.removeEventListener('AppleIDSignInOnFailure', onError)
    }
  }, [])
}

export default useAppleLogin
